import './index.scss';

import {
  createChart,
  CrosshairMode,
  IChartApi,
  IPriceLine,
  ISeriesApi,
  LineStyle,
  Logical,
  PriceScaleMode,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useUpdateEffect } from 'react-use';

import { useThemeColors } from '@/shared/theme';

import { useRelatedChartsEvent } from 'hooks/charts/useRelatedChartsEvent';
import { chartVolumeBarColor, internalVolumeBarColor } from 'shared/consts/charts';
import { Bus } from 'tools';
import { ECexOrderSide } from 'types/cex/orders';
import { chartFormatter } from 'utils/charts';
import { EExchange } from 'web3';

import { PairTooltip } from './PairTooltip';

import { ChartLoading } from '../controls/ChartLoading';
import { ChartNotFound } from '../controls/ChartNotFound';

interface IPairChartProps {
  loading: boolean;
  chart: string;
  relatedCharts?: string[];
  logicalEvent: string;
  cursorEvent: string;
  isConverted?: boolean;
  exchange: EExchange;
  candles: {
    time: UTCTimestamp;
    close: number;
    high: number;
    low: number;
    open: number;
  }[];
  volumes: {
    time: UTCTimestamp;
    value: number;
  }[];
  internal_volumes?: {
    time: UTCTimestamp;
    value: number;
  }[];
  orders?: { price: number; amount: string; side: ECexOrderSide; label: string }[];
  rightScaleMinWidth?: number;
  /** Text for an empty chart. Without the text the chart shows "Chart not found". */
  notFoundText?: string;
  /** Shows the Retry button on an empty chart. */
  onRetry?: () => void;
}

const PairChart: React.FC<IPairChartProps> = memo(
  ({
    loading,
    chart: chartId,
    relatedCharts = [],
    logicalEvent,
    cursorEvent,
    isConverted,
    exchange,
    candles,
    internal_volumes,
    volumes,
    orders = [],
    rightScaleMinWidth,
    notFoundText,
    onRetry,
  }) => {
    const palette = useThemeColors();
    const chartRef = useRef<HTMLDivElement | null>(null);
    const chartAPIRef = useRef<IChartApi | undefined>(undefined);

    const candlestickSeriesRef = useRef<ISeriesApi<'Candlestick'> | undefined>(undefined);
    const totalvolumeSeriesRef = useRef<ISeriesApi<'Histogram'> | undefined>(undefined);
    const internalvolumeSeriesRef = useRef<ISeriesApi<'Histogram'> | undefined>(undefined);
    const tradePriceLines = useRef<IPriceLine[]>([]);

    const initChart = useCallback(() => {
      try {
        try {
          chartAPIRef.current?.remove();
        } catch (error) {}

        const chart = createChart(chartRef.current as HTMLElement, {
          autoSize: true,
          crosshair: {
            mode: CrosshairMode.Normal,
          },
          rightPriceScale: {
            mode: PriceScaleMode.Normal,
            borderColor: palette.chart_border_color,
            alignLabels: false,
            minimumWidth: rightScaleMinWidth,
          },
          grid: {
            horzLines: {
              color: palette.chart_line_color,
            },
            vertLines: {
              color: palette.chart_line_color,
            },
          },
          layout: {
            background: {
              color: 'transparent',
            },
            textColor: '#eee',
          },
          timeScale: {
            timeVisible: true,
            secondsVisible: false,
            borderColor: palette.chart_border_color,
            rightBarStaysOnScroll: true,
          },
          localization: {
            priceFormatter: chartFormatter,
            percentageFormatter: (p: number) => `${p.toFixed(2)}%`,
          },
        });
        chartAPIRef.current = chart;

        //candlestick
        const candleStickSeries = chart.addCandlestickSeries({
          priceFormat: {
            type: 'price',
            precision: 7,
            minMove: 0.0000001,
          },
        });
        candleStickSeries.priceScale().applyOptions({ scaleMargins: { top: 0.1, bottom: 0.2 } });

        //total volume
        const totalVolumeSeries = chart.addHistogramSeries({
          color: chartVolumeBarColor,
          priceFormat: {
            type: 'volume',
          },
          priceScaleId: '',
          lastValueVisible: false,
        });
        totalVolumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });

        //internal volume
        const internalVolumeSeries = chart.addHistogramSeries({
          color: internalVolumeBarColor,
          priceFormat: {
            type: 'volume',
          },
          priceScaleId: '',
          lastValueVisible: false,
        });

        internalVolumeSeries.priceScale().applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        internalvolumeSeriesRef.current = internalVolumeSeries;

        // save series
        candlestickSeriesRef.current = candleStickSeries;
        totalvolumeSeriesRef.current = totalVolumeSeries;
        // -----

        candlestickSeriesRef.current.setData(candles);
        totalvolumeSeriesRef.current.setData(volumes);
        if (internal_volumes) {
          internalvolumeSeriesRef.current.setData(internal_volumes);
        }

        chart.timeScale().subscribeVisibleLogicalRangeChange(logicalRange => {
          if (!logicalRange) return;

          Bus.emit(`${chartId}-${logicalEvent}`, {
            from: Number(logicalRange.from),
            to: Number(logicalRange.to),
          });
        });

        chart.subscribeCrosshairMove(param => {
          if (!param.sourceEvent) return;

          if (param.time) {
            Bus.emit(`${chartId}-${cursorEvent}`, {
              time: param.time,
              logical: param.logical,
            });
          } else {
            chartAPIRef.current?.clearCrosshairPosition();
            Bus.emit(`${chartId}-${cursorEvent}`, undefined);
          }
        });

        if (orders.length !== 0) {
          const priceLines: IPriceLine[] = [];

          for (const trade of orders) {
            const priceLine = candleStickSeries.createPriceLine({
              price: trade.price,
              title: trade.label,
              color:
                trade.side === ECexOrderSide.sell
                  ? palette.sell_trade_color
                  : palette.buy_trade_color,
              lineWidth: 1,
              lineStyle: LineStyle.Solid,
              axisLabelVisible: true,
            });

            priceLines.push(priceLine);
          }

          tradePriceLines.current = priceLines;
        }

        // -----
      } catch (error) {
        console.error('pair chart initing error: ', error);
      }
    }, [
      rightScaleMinWidth,
      chartId,
      logicalEvent,
      cursorEvent,
      orders,
      palette,
      candles,
      internal_volumes,
      volumes,
    ]);

    const logicalSetter = useCallback((v: unknown) => {
      const _v = v as unknown as { from: number; to: number };

      if (chartAPIRef && chartAPIRef.current) {
        chartAPIRef.current.timeScale().setVisibleLogicalRange(_v);
      }
    }, []);

    const cursorSetter = useCallback((v: unknown) => {
      if (chartAPIRef.current) {
        const _v = v as unknown as { time: Time; logical: Logical | undefined } | undefined;

        if (!_v) {
          chartAPIRef.current.clearCrosshairPosition();
        } else if (candlestickSeriesRef.current) {
          if (_v.logical) {
            const candle = candles[_v.logical];

            chartAPIRef.current.setCrosshairPosition(
              candle ? candle.close : 0,
              _v.time,
              candlestickSeriesRef.current,
            );
          } else {
            chartAPIRef.current.setCrosshairPosition(0, _v.time, candlestickSeriesRef.current);
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useRelatedChartsEvent({
      events: (relatedCharts ?? []).map(ch => `${ch}-${logicalEvent}`),
      handler: logicalSetter,
    });

    useRelatedChartsEvent({
      events: (relatedCharts ?? []).map(ch => `${ch}-${cursorEvent}`),
      handler: cursorSetter,
    });

    useEffect(() => {
      const paginationHandler = (data: unknown) => {
        const { candleStickRecords, totalVolumeRecords, internalVolumeRecords } = data as any;

        if (candlestickSeriesRef.current) {
          candlestickSeriesRef.current.setData(candleStickRecords);
        }

        if (totalvolumeSeriesRef.current) {
          totalvolumeSeriesRef.current.setData(totalVolumeRecords);
        }

        if (internalvolumeSeriesRef.current && internalVolumeRecords) {
          internalvolumeSeriesRef.current.setData(internalVolumeRecords);
        }
      };

      const updateHandler = (data: unknown) => {
        const { candleStickRecord, totalVolumeRecord, internalVolumeRecord } = data as any;

        if (candlestickSeriesRef.current) {
          candlestickSeriesRef.current.update(candleStickRecord);
        }

        if (totalvolumeSeriesRef.current) {
          totalvolumeSeriesRef.current.update(totalVolumeRecord);
        }

        if (internalvolumeSeriesRef.current && internalVolumeRecord) {
          internalvolumeSeriesRef.current.update(internalVolumeRecord);
        }
      };

      Bus.on(`${chartId}-update-pagination`, paginationHandler);
      Bus.on(`${chartId}-update-kline`, updateHandler);

      return () => {
        Bus.off(`${chartId}-update-pagination`, paginationHandler);
        Bus.off(`${chartId}-update-kline`, updateHandler);
      };
    }, [chartId]);

    useUpdateEffect(() => {
      if (chartAPIRef.current) {
        chartAPIRef.current.applyOptions({
          rightPriceScale: {
            borderColor: palette.chart_border_color,
          },
          grid: {
            horzLines: {
              color: palette.chart_line_color,
            },
            vertLines: {
              color: palette.chart_line_color,
            },
          },
          timeScale: {
            borderColor: palette.chart_border_color,
          },
        });
      }
    }, [palette]);

    useUpdateEffect(() => {
      for (const priceLine of tradePriceLines.current) {
        priceLine.applyOptions({
          lineVisible: false,
          axisLabelVisible: false,
        });
      }

      if (orders.length !== 0 && candlestickSeriesRef.current) {
        const priceLines: IPriceLine[] = [];

        for (const order of orders) {
          const priceLine = candlestickSeriesRef.current.createPriceLine({
            price: order.price,
            title: order.label,
            color:
              order.side === ECexOrderSide.sell
                ? palette.sell_trade_color
                : palette.buy_trade_color,
            lineWidth: 1,
            lineStyle: LineStyle.Solid,
            axisLabelVisible: true,
          });

          priceLines.push(priceLine);
        }

        tradePriceLines.current = priceLines;
      }
    }, [orders]);

    useEffect(() => {
      if (loading || candles.length === 0) return;

      if (!candlestickSeriesRef.current || !totalvolumeSeriesRef.current) {
        initChart();
        return;
      }

      candlestickSeriesRef.current.setData(candles);
      totalvolumeSeriesRef.current.setData(volumes);
      internalvolumeSeriesRef.current?.setData(internal_volumes ?? []);
    }, [candles, initChart, internal_volumes, loading, volumes]);

    return (
      <div className="mm-pair-chart-container">
        {candles.length !== 0 && (
          <PairTooltip
            chartId={chartId}
            relatedCharts={relatedCharts}
            isConverted={isConverted}
            exchange={exchange}
            volumes={volumes}
            internal_volumes={internal_volumes}
            candles={candles}
            cursorEvent={cursorEvent}
            logicalEvent={logicalEvent}
          />
        )}
        <div className="chart-wrp">
          <div
            className="chart"
            ref={node => {
              chartRef.current = node;
            }}
          />
        </div>

        {loading && <ChartLoading />}
        {!loading && candles.length === 0 && (
          <ChartNotFound text={notFoundText ?? 'Chart not found'} onRetry={onRetry} />
        )}
      </div>
    );
  },
);

export { PairChart };
